/**
 * ChatSidebarLeft - Sidebar esquerda com tabs e lista de conversas
 */

import React, { useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContacts, useCreateContact, useUpdateContact, useDeleteContact, useMergeContacts, useChatSocket, useUnreadCountByType, useContactSync, useChatPreferences } from '../hooks';
import { useMessageSoundPreference } from '@/modules/chat/hooks/useMessageSoundPreference';
import { useBlockContact, useUnblockContact } from '@/modules/contacts/hooks/useContactMutations';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import type { SidebarView } from '../types';
import { ChatFiltersBar } from './ChatFiltersBar';
import { countActiveFilters } from './ChatFilterDefinitions';
import AddContactDialog, { ContactFormData } from './AddContactDialog';
import EditContactDialog, { EditContactFormData } from './EditContactDialog';
import DeleteContactDialog from './DeleteContactDialog';
import MergeContactsDialog from './MergeContactsDialog';
import { StartConversationDialog } from './StartConversationDialog';
import { ConversationList } from './ConversationList'; // ✅ Infinite scroll component
import { HistoryList } from './HistoryList'; // ✅ Histórico agrupado por (contato, canal)
import { PrivacyText } from '@/shared/components/ui/PrivacyText';
import { CardActionsMenu, FiltersButton, ToolbarSearch, Tooltip } from '@/shared/components/ui';
import { toast } from 'sonner';
import type { ContactResponse } from '@troia-v3/types';
import { getInitials } from '../utils/dateFormatter';
import { formatPhoneNumber } from '@/shared/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { HEADER_HEIGHT } from '@/shared/constants/layout';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
interface ChatSidebarLeftProps {
  expanded: boolean;
  onToggle: () => void;
  selectedConversationId: string | null;
  /** Callback ao selecionar conversa - passa o ID e opcionalmente o termo de busca atual */
  onSelectConversation: (id: string, searchTerm?: string) => void;
  initialView: SidebarView | null;
}

const ChatSidebarLeft: React.FC<ChatSidebarLeftProps> = ({
  expanded,
  onToggle,
  selectedConversationId,
  onSelectConversation,
  initialView,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canManageContacts, hasModulePermission } = usePermissions();

  // A aba Contatos abre com `contacts:read`, mas bloquear/desbloquear exige
  // `contacts:update` no servidor (as duas rotas passam por
  // `permissionMiddleware(requirePermission('contacts','update'))`). Sem este
  // gate, quem só lê clica, confirma, e não acontece nada — mesma permissão que
  // o kebab do ChatBox já usa.
  const canUpdateContacts = hasModulePermission('contacts', 'update');

  // Chat preferences (persisted in localStorage)
  const {
    currentView: storedView,
    setCurrentView,
    showOnlyMyAttendances,
    setShowOnlyMyAttendances,
    chatFilters: storedFilters,
    setChatFilters: setStoredFilters
  } = useChatPreferences();

  // ✅ Se há initialView (de conversationId), usar ela; caso contrário, usar localStorage
  const [currentView, setCurrentViewState] = useState<SidebarView>(() => {
    const initial = initialView || storedView;
    // Se a view salva é 'contacts' mas o usuário não tem permissão, fallback para 'chats'
    if (initial === 'contacts' && !canManageContacts()) return 'chats';
    return initial;
  });

  // ✅ Sincronizar view com initialView quando mudar
  React.useEffect(() => {
    if (initialView) {
      setCurrentViewState(initialView);
      // Atualizar localStorage para manter consistência
      setCurrentView(initialView);
    }
  }, [initialView, setCurrentView]);

  // ✅ Wrapper para setCurrentView que atualiza estado local + localStorage e
  // sincroniza a ROTA: aba Histórico vive em /history (2026-07-02), as demais em /chat.
  // Só clique do usuário navega — o sync via initialView (efeito acima) não passa aqui.
  const handleSetCurrentView = React.useCallback((view: SidebarView) => {
    setCurrentViewState(view);
    setCurrentView(view);
    const onHistoryRoute = location.pathname === '/history' || /^\/history\/[^/]+\/[^/]+$/.test(location.pathname);
    if (view === 'history' && !onHistoryRoute) {
      navigate('/history');
    } else if (view !== 'history' && onHistoryRoute) {
      navigate('/chat');
    }
  }, [setCurrentView, navigate, location.pathname]);

  // ✅ Filtros com persistência no localStorage (via useChatPreferences)
  const filters = storedFilters;
  const setFilters = setStoredFilters;
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const activeFilterCount = useMemo(() => countActiveFilters(filters), [filters]);
  // Mutável: o botão de filtro tem duas instâncias (desktop na linha de ferramentas,
  // mobile ao lado da busca) — quem abre o popover grava o próprio elemento no ref.
  const filterTriggerRef = useRef<HTMLButtonElement | null>(null);
  // Âncoras dos FiltersButton (o primitivo não expõe ref do botão — capturamos via wrapper)
  const mobileFilterAnchorRef = useRef<HTMLSpanElement | null>(null);
  const desktopFilterAnchorRef = useRef<HTMLSpanElement | null>(null);
  const [isAddContactDialogOpen, setIsAddContactDialogOpen] = useState(false);
  const [isEditContactDialogOpen, setIsEditContactDialogOpen] = useState(false);
  const [isDeleteContactDialogOpen, setIsDeleteContactDialogOpen] = useState(false);
  const [isMergeContactsDialogOpen, setIsMergeContactsDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactResponse | null>(null);
  // Estado dedicado da modal "Iniciar Atendimento" (não colide com selectedContact de editar/excluir)
  const [isStartConversationOpen, setIsStartConversationOpen] = useState(false);
  const [contactForConversation, setContactForConversation] = useState<ContactResponse | null>(null);
  // Filtro "Só bloqueados" (2026-09-01) — aba Contatos, dropdown "Mais opções".
  // Sem ele, desbloquear depende de o atendente lembrar de quem ele bloqueou.
  const [showOnlyBlockedContacts, setShowOnlyBlockedContacts] = useState(false);

  const messageSound = useMessageSoundPreference();

  // Listeners de conversa ativa (read receipts, status de mensagem, conversation:updated).
  // Atualização da lista de conversas vive em `useConversationListSocket` (global,
  // via GlobalSocketListeners) — toca som/toast via `useGlobalMessageSocket`.
  useChatSocket({ enabled: true });

  // Buscar contatos (somente quando a view de contatos está ativa)
  const { data: contactsData, isLoading: isLoadingContacts } = useContacts(
    {
      page: 1,
      limit: 50,
      search: filters.search || '',
      filters: {
        ...(showOnlyBlockedContacts ? { blocked: true } : {}),
      },
    },
    currentView === 'contacts'
  );

  // Mutations
  const { mutate: createContact, isPending: isCreatingContact } = useCreateContact();
  const { mutate: updateContact, isPending: isUpdatingContact } = useUpdateContact();
  const { mutate: deleteContact, isPending: isDeletingContact } = useDeleteContact();
  const { mutate: mergeContacts, isPending: isMergingContacts } = useMergeContacts();
  // const { mutate: createConversationFromContact } = useCreateConversationFromContact();

  // Bloqueio de contato (2026-09-01) — toggle no kebab do item da aba Contatos.
  const confirm = useConfirm();
  const blockContactMutation = useBlockContact();
  const unblockContactMutation = useUnblockContact();

  // ✅ Contact Sync Hook - Escuta eventos de sincronização WhatsApp em tempo real
  useContactSync({
    enabled: true,
    onSyncSuccess: (_payload) => {
      toast.success('✅ WhatsApp sincronizado!', {
        duration: 3000,
        icon: '📱',
      });
    },
    onSyncFailed: (payload) => {
      toast.error(`❌ Falha ao sincronizar WhatsApp: ${payload.error}`, {
        duration: 5000,
      });
      console.error('❌ Contact sync failed:', payload);
    }
  });

  // ✅ Contadores de não lidas por tipo de conversa (para badges das tabs)
  const { data: aiUnreadCount = 0 } = useUnreadCountByType({
    conversationType: 'ai',
    refetchInterval: 30000 // Auto-refresh every 30 seconds
  });

  const { data: chatsUnreadCount = 0 } = useUnreadCountByType({
    conversationType: 'individual',
    refetchInterval: 30000
  });

  const { data: groupsUnreadCount = 0 } = useUnreadCountByType({
    conversationType: 'group',
    refetchInterval: 30000
  });

  const tabs = [
    { id: 'ai' as SidebarView, label: 'IA', icon: 'zap', unreadCount: aiUnreadCount },
    { id: 'chats' as SidebarView, label: 'Chats', icon: 'message-square', unreadCount: chatsUnreadCount },
    { id: 'groups' as SidebarView, label: 'Grupos', icon: 'users', unreadCount: groupsUnreadCount },
    ...(canManageContacts() ? [{ id: 'contacts' as SidebarView, label: 'Contatos', icon: 'user', unreadCount: 0 }] : []),
    { id: 'history' as SidebarView, label: 'Histórico', icon: 'clock', unreadCount: 0 },
  ];

  // ✅ Reset toggle quando mudar de view
  React.useEffect(() => {
    if (currentView !== 'chats') {
      setShowOnlyMyAttendances(false);
    }
  }, [currentView]);

  // ✅ Compute effective filters with assigned flag when toggle is active (apenas para Chats)
  const effectiveFilters = useMemo(() => {
    if (!showOnlyMyAttendances || currentView !== 'chats') return filters;
    return {
      ...filters,
      assigned: true  // ConversationList expects boolean, not assigneeId string
    };
  }, [filters, showOnlyMyAttendances, currentView]);

  // Handlers para contatos
  const handleSaveContact = (data: ContactFormData, avatar?: File) => {
    createContact({ data, avatar }, {
      onSuccess: () => {
        toast.success('Contato adicionado com sucesso!');
        setIsAddContactDialogOpen(false);
      },
      onError: (error: any) => {
        if (error.response?.status === 402 || error.response?.status === 403) return;
        toast.error('Erro ao adicionar contato');
      },
    });
  };

  // Clique no contato não navega mais — a rota /history/:contactId foi removida
  // em 2026-07-02 (histórico agora é por par contato+canal em /history/:c/:ch).
  // Ações no contato ficam só nos botões (editar/excluir/iniciar conversa).

  const handleStartConversation = (contact: ContactResponse) => {
    setContactForConversation(contact);
    setIsStartConversationOpen(true);
  };

  const handleEditContact = (contact: ContactResponse) => {
    setSelectedContact(contact);
    setIsEditContactDialogOpen(true);
  };

  const handleUpdateContact = (id: string, data: EditContactFormData, avatar?: File) => {
    updateContact({ id, data, avatar }, {
      onSuccess: () => {
        toast.success('Contato atualizado com sucesso!');
        setIsEditContactDialogOpen(false);
        setSelectedContact(null);
      },
      onError: (error) => {
        toast.error('Erro ao atualizar contato');
        console.error(error);
      },
    });
  };

  const handleDeleteContactClick = (contact: ContactResponse) => {
    setSelectedContact(contact);
    setIsDeleteContactDialogOpen(true);
  };

  const handleConfirmDelete = (id: string) => {
    deleteContact(id, {
      onSuccess: () => {
        toast.success('Contato deletado com sucesso!');
        setIsDeleteContactDialogOpen(false);
        setSelectedContact(null);
      },
      onError: (error) => {
        toast.error('Erro ao deletar contato');
        console.error(error);
      },
    });
  };

  /**
   * Bloqueio de contato (2026-09-01) — mesma confirmação do kebab do ChatBox
   * (`handleToggleBlock`): é a mesma ação, e dois textos diferentes para o
   * mesmo efeito confundem o atendente.
   */
  const handleToggleBlockContact = async (contact: ContactResponse): Promise<void> => {
    if (contact.blocked) {
      unblockContactMutation.mutate(contact.id);
      return;
    }

    if (!(await confirm({
      title: 'Bloquear contato',
      message: `Bloquear "${contact.name}"? As próximas mensagens dele não vão mais chegar, e nada será enviado a ele. A conversa atual será encerrada.`,
      confirmButtonText: 'Bloquear',
      variant: 'danger',
    }))) return;

    blockContactMutation.mutate(contact.id);
  };

  const handleMergeContacts = (sourceContactId: string, targetContactId: string, name: string) => {
    mergeContacts({ sourceContactId, targetContactId, name }, {
      onSuccess: () => {
        toast.success('Contatos mesclados com sucesso!');
        setIsMergeContactsDialogOpen(false);
      },
      onError: (error) => {
        toast.error('Erro ao mesclar contatos');
        console.error(error);
      },
    });
  };

  // Mensagens de empty state específicas por view
  // const getEmptyStateTitle = () => {
  //   switch (currentView) {
  //     case 'ai':
  //       return 'Nenhuma conversa com IA';
  //     case 'chats':
  //       return 'Nenhum chat ativo';
  //     case 'groups':
  //       return 'Nenhum grupo';
  //     case 'contacts':
  //       return 'Nenhum contato';
  //     case 'history':
  //       return 'Nenhuma conversa finalizada';
  //     default:
  //       return 'Nenhuma conversa';
  //   }
  // };

  // const getEmptyStateDescription = () => {
  //   switch (currentView) {
  //     case 'ai':
  //       return 'Conversas com assistente de IA aparecerão aqui';
  //     case 'chats':
  //       return 'Novas conversas aparecerão aqui quando iniciadas';
  //     case 'groups':
  //       return 'Grupos de conversa aparecerão aqui';
  //     case 'contacts':
  //       return 'Seus contatos aparecerão aqui';
  //     case 'history':
  //       return 'Conversas finalizadas aparecerão aqui';
  //     default:
  //       return 'As conversas aparecerão aqui quando iniciadas';
  //   }
  // };

  const renderContactItem = (contact: ContactResponse) => {
    return (
      <div
        key={contact.id}
        className={`transition-colors group select-none ${
          expanded ? 'mx-1.5 my-0.5 px-2 py-[9px] rounded-lg' : 'py-2 px-0 flex justify-center'
        }`}
        onClick={() => handleStartConversation(contact)}
        title={expanded ? 'Iniciar atendimento' : undefined}
        role="button"
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <div className={`flex items-center ${expanded ? 'gap-[10px]' : ''}`}>
          {/* Avatar usando componente centralizado (sem foto = slate #64748b) */}
          <div title={!expanded ? contact.name : undefined}>
            <Avatar className="w-10 h-10" isSquircle>
              <AvatarImage
                src={contact.picture}
                alt={contact.name}
                isSquircle
              />
              <AvatarFallback isSquircle>
                <span className={`text-white font-semibold ${expanded ? 'text-sm' : 'text-xs'}`}>
                  {getInitials(contact.name)}
                </span>
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Content */}
          {expanded && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 min-w-0">
                <h4
                  className="font-semibold text-sm truncate"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {contact.name}
                </h4>
                {contact.blocked === true && (
                  // `Icon` não repassa props além de size/className/style/onClick —
                  // title e data-testid têm que ir no wrapper, senão somem do DOM.
                  <span title="Contato bloqueado" data-testid={`contact-blocked-${contact.id}`}>
                    <Icon
                      name="slash"
                      className="w-3.5 h-3.5 flex-shrink-0"
                      style={{ color: 'var(--theme-error-fg)' }}
                    />
                  </span>
                )}
              </div>
              <p
                className="text-xs truncate"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                <PrivacyText>{contact.identifiers.phone[0] ? formatPhoneNumber(contact.identifiers.phone[0]) : 'Sem telefone'}</PrivacyText>
              </p>
            </div>
          )}

          {/* Actions Menu (kebab): iniciar atendimento / editar / excluir */}
          {expanded && (
            <div className="flex-shrink-0">
              <CardActionsMenu
                data-testid={`contact-actions-${contact.id}`}
                actions={[
                  { label: 'Iniciar atendimento', icon: 'message-circle', onClick: () => handleStartConversation(contact) },
                  { label: 'Editar', icon: 'edit', onClick: () => handleEditContact(contact) },
                  ...(canUpdateContacts
                    ? [{
                        label: contact.blocked ? 'Desbloquear' : 'Bloquear',
                        icon: (contact.blocked ? 'unlock' : 'slash') as IconName,
                        ...(contact.blocked ? {} : { variant: 'danger' as const }),
                        onClick: () => void handleToggleBlockContact(contact),
                      }]
                    : []),
                  { label: 'Excluir', icon: 'trash', variant: 'danger', onClick: () => handleDeleteContactClick(contact) },
                ]}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  // ✅ renderConversationItem removido - agora usa ConversationItem component via ConversationList

  return (
    <div
      className={`h-full flex flex-col lg:border-r transition-all duration-300 ${!expanded ? 'lg:w-16' : ''}`}
      style={{
        backgroundColor: 'var(--theme-sidebar-background)',
        borderColor: 'var(--theme-sidebar-border)'
      }}
    >
      {/* Tabs — ícone + label, sublinhado no ativo (DS chat redesign) */}
      <div
        className={`flex flex-shrink-0 overflow-visible ${expanded ? 'flex-row border-b px-1.5' : 'flex-col gap-1 py-2 px-1'}`}
        style={{
          borderColor: 'var(--theme-sidebar-border)',
          // Trava no CONTAINER, nunca no padding do botão: o badge de não-lidas do
          // modo expandido está posicionado em absoluto (top:5px) calibrado ao
          // padding-top de 11px. Os botões são flex-1 e o align-items default
          // (stretch) faz eles preencherem os 56px sozinhos.
          // O rail (colapsado) fica fora da trava — lá as abas são coluna vertical.
          ...(expanded ? { height: HEADER_HEIGHT, minHeight: HEADER_HEIGHT } : {}),
        }}
        data-testid="chat-sidebar-tabs"
      >
        {tabs.map((tab) => {
          const isActive = currentView === tab.id;

          const tabButton = (
            <button
              key={tab.id}
              onClick={() => handleSetCurrentView(tab.id)}
              className={`
                ${expanded
                  ? 'flex-1 flex flex-col items-center justify-center gap-[3px] border-b-2 -mb-px relative'
                  : 'w-full py-3 px-2 flex items-center justify-center border-l-2 relative'
                }
                transition-colors
              `}
              style={{
                ...(expanded ? { padding: '11px 2px 9px' } : {}),
                borderColor: isActive ? 'var(--theme-sidebar-active)' : 'transparent',
                color: isActive ? 'var(--theme-sidebar-active)' : 'var(--theme-sidebar-text)',
                backgroundColor: isActive && !expanded ? 'var(--theme-button-ghost-hover)' : 'transparent'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = 'var(--theme-sidebar-text-hover)';
                  if (!expanded) {
                    e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                  }
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = 'var(--theme-sidebar-text)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                } else if (isActive && !expanded) {
                  e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                }
              }}
              data-testid={`chat-sidebar-tab-${tab.id}`}
            >
              <Icon name={tab.icon as IconName} className={expanded ? 'w-[19px] h-[19px]' : 'w-5 h-5'} />
              {expanded && (
                <span className="font-semibold" style={{ fontSize: '10.5px', lineHeight: '1' }}>{tab.label}</span>
              )}
              {/* Badge contador de não lidas - posicionado no topo-centro do botão */}
              {tab.unreadCount > 0 && (
                <span
                  className={`absolute z-10 flex items-center justify-center font-bold text-white rounded-full ${expanded ? '' : '-top-1 -right-1'}`}
                  style={{
                    backgroundColor: '#ef4444',
                    minWidth: '15px',
                    height: '15px',
                    padding: '0 4px',
                    fontSize: '8px',
                    lineHeight: '1',
                    ...(expanded ? { top: '5px', right: 'calc(50% - 20px)' } : {})
                  }}
                >
                  {tab.unreadCount > 99 ? '99+' : tab.unreadCount}
                </span>
              )}
            </button>
          );

          // Colapsada: botão só-ícone → Tooltip shared à direita (F80, title= nativo some).
          // Expandida: o label é visível — tooltip seria redundante.
          return expanded ? (
            tabButton
          ) : (
            <Tooltip key={tab.id} content={tab.label} side="right">
              {tabButton}
            </Tooltip>
          );
        })}
      </div>

      {/* Header com busca, ações e chips (DS chat redesign) */}
      <div
        className={`border-b flex-shrink-0 ${!expanded ? 'flex items-center justify-center' : ''}`}
        style={{
          borderColor: 'var(--theme-sidebar-border)',
          ...(expanded ? { padding: '10px 12px' } : { height: HEADER_HEIGHT }),
        }}
      >
        {expanded ? (
          <>
            {/* Busca + ferramentas empilhadas */}
            <div className="flex flex-col" style={{ gap: '9px' }}>
              {/* Linha de busca — no mobile a linha de ferramentas colapsa em
                  botões compactos aqui (anatomia `.m-search` do mockup mobile) */}
              <div className="flex items-center gap-1.5">
              {/* Caixa de busca — ToolbarSearch shared (F17: 38px, foco, X de limpar) */}
              <ToolbarSearch
                value={filters.search || ''}
                onChange={(val) => {
                  const newFilters = { ...filters };
                  if (val) {
                    newFilters.search = val;
                  } else {
                    delete newFilters.search;
                  }
                  setFilters(newFilters);
                }}
                placeholder={currentView === 'contacts' ? 'Buscar contatos…' : currentView === 'history' ? 'Buscar no histórico…' : 'Buscar conversas…'}
                shrinkClass="flex-1 min-w-0 !max-w-none"
                data-testid="chat-sidebar-search"
              />

              {/* Botões compactos SÓ MOBILE (a linha de ferramentas do desktop some <lg) */}
              {currentView !== 'history' && (
                <span ref={mobileFilterAnchorRef} className="lg:hidden flex-none">
                  <FiltersButton
                    count={activeFilterCount}
                    active={isFilterPopoverOpen}
                    onClick={() => {
                      const btn = mobileFilterAnchorRef.current?.querySelector('button');
                      if (btn) filterTriggerRef.current = btn;
                      setIsFilterPopoverOpen(true);
                    }}
                    data-testid="chat-mobile-filters"
                  />
                </span>
              )}
              {(currentView === 'chats' || currentView === 'ai' || currentView === 'groups') && (
                <button
                  onClick={() => messageSound.toggle()}
                  disabled={messageSound.isPending}
                  className="lg:hidden flex-shrink-0 inline-flex items-center justify-center rounded-lg border transition-colors disabled:opacity-50"
                  style={{ width: '36px', height: '36px', color: 'var(--theme-sidebar-text)', borderColor: 'var(--theme-input-border)' }}
                  aria-label={messageSound.isMuted ? 'Ativar som de mensagens' : 'Silenciar som de mensagens'}
                  aria-pressed={messageSound.isMuted}
                  data-testid="chat-mobile-mute-toggle"
                >
                  <Icon name={messageSound.isMuted ? 'bell-off' : 'bell'} className="h-[18px] w-[18px]" />
                </button>
              )}
              {currentView === 'contacts' && (
                <button
                  onClick={() => setIsAddContactDialogOpen(true)}
                  className="lg:hidden flex-shrink-0 inline-flex items-center justify-center rounded-lg border transition-colors"
                  style={{ width: '36px', height: '36px', color: 'var(--theme-sidebar-text)', borderColor: 'var(--theme-input-border)' }}
                  aria-label="Adicionar contato"
                  data-testid="chat-mobile-add-contact"
                >
                  <Icon name="user-plus" className="h-[18px] w-[18px]" />
                </button>
              )}
              </div>

              {/* Ferramentas: pílula à esquerda + botões de ícone bordeados (desktop) */}
              <div className="hidden lg:flex items-center gap-1.5">
                {/* Toggle "Meus atendimentos" como pílula (apenas view Chats) */}
                {currentView === 'chats' && (
                  <button
                    onClick={() => setShowOnlyMyAttendances(!showOnlyMyAttendances)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border transition-colors"
                    style={{
                      height: '36px',
                      padding: '0 11px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: showOnlyMyAttendances ? 'var(--theme-sidebar-active-text)' : 'var(--theme-card-text)',
                      backgroundColor: showOnlyMyAttendances ? 'var(--theme-sidebar-active)' : 'transparent',
                      borderColor: showOnlyMyAttendances ? 'transparent' : 'var(--theme-input-border)',
                    }}
                    onMouseEnter={(e) => {
                      if (!showOnlyMyAttendances) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!showOnlyMyAttendances) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Icon name="user" className="h-[18px] w-[18px]" />
                    <span>Meus atendimentos</span>
                  </button>
                )}
                {/* Botão adicionar contato como pílula (apenas view Contatos) */}
                {currentView === 'contacts' && (
                  <button
                    onClick={() => setIsAddContactDialogOpen(true)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border transition-colors"
                    style={{
                      height: '36px',
                      padding: '0 11px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: 'var(--theme-card-text)',
                      borderColor: 'var(--theme-input-border)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Icon name="user-plus" className="h-[18px] w-[18px]" />
                    <span>Adicionar contato</span>
                  </button>
                )}
                {/* Espaçador quando não há pílula à esquerda (IA/Grupos/Histórico) */}
                {currentView !== 'chats' && currentView !== 'contacts' && (
                  <span className="flex-1" />
                )}
                {/* Dropdown "Mais opções" (apenas view Contatos) */}
                {currentView === 'contacts' && (
                  <DropdownMenu>
                    <Tooltip content="Mais opções" side="bottom">
                      <DropdownMenuTrigger asChild>
                        <button
                          className="flex-shrink-0 inline-flex items-center justify-center rounded-lg border transition-colors"
                          style={{ width: '36px', height: '36px', color: 'var(--theme-sidebar-text)', borderColor: 'var(--theme-input-border)' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          data-testid="chat-sidebar-more-options"
                        >
                          <Icon name="more-vertical" className="h-[18px] w-[18px]" />
                        </button>
                      </DropdownMenuTrigger>
                    </Tooltip>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => setIsMergeContactsDialogOpen(true)}
                        disabled={!contactsData?.items || contactsData.items.length < 2}
                      >
                        <Icon name="user-plus" className="w-4 h-4 mr-2" />
                        Mesclar Contatos
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setShowOnlyBlockedContacts((v) => !v)}
                        data-testid="contacts-filter-blocked"
                      >
                        <Icon name={showOnlyBlockedContacts ? 'check' : 'slash'} className="w-4 h-4 mr-2" />
                        Só bloqueados
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {/* Botão de filtro no header — Histórico usa só busca (decisão 2026-07-02) */}
                {currentView !== 'history' && (
                  <span ref={desktopFilterAnchorRef} className="flex-none">
                    <FiltersButton
                      count={activeFilterCount}
                      active={isFilterPopoverOpen}
                      onClick={() => {
                        const btn = desktopFilterAnchorRef.current?.querySelector('button');
                        if (btn) filterTriggerRef.current = btn;
                        setIsFilterPopoverOpen(true);
                      }}
                      data-testid="chat-sidebar-filters"
                    />
                  </span>
                )}
                <Tooltip
                  content={messageSound.isMuted ? 'Ativar som de mensagens' : 'Silenciar som de mensagens'}
                  side="bottom"
                >
                  <button
                    onClick={() => messageSound.toggle()}
                    disabled={messageSound.isPending}
                    className="flex-shrink-0 inline-flex items-center justify-center rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ width: '36px', height: '36px', color: 'var(--theme-sidebar-text)', borderColor: 'var(--theme-input-border)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    data-testid="chat-sidebar-mute-toggle"
                    aria-label={messageSound.isMuted ? 'Ativar som de mensagens' : 'Silenciar som de mensagens'}
                    aria-pressed={messageSound.isMuted}
                  >
                    <Icon
                      name={messageSound.isMuted ? 'bell-off' : 'bell'}
                      className="h-[18px] w-[18px]"
                    />
                  </button>
                </Tooltip>
                <Tooltip content="Recolher" side="bottom">
                  <button
                    onClick={onToggle}
                    className="flex-shrink-0 inline-flex items-center justify-center rounded-lg border transition-colors"
                    style={{ width: '36px', height: '36px', color: 'var(--theme-sidebar-text)', borderColor: 'var(--theme-input-border)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    data-testid="chat-sidebar-collapse"
                  >
                    <Icon name="chevron-left" className="h-[18px] w-[18px]" />
                  </button>
                </Tooltip>
              </div>
            </div>

            {/* ✅ ChatFiltersBar - Barra de chips (Histórico usa só busca, sem chips) */}
            {currentView !== 'history' && (
              <ChatFiltersBar
                viewType={currentView}
                filters={filters}
                onFiltersChange={setFilters}
                showOperatorFilter={true}
                externalTriggerRef={filterTriggerRef}
                isPopoverOpen={isFilterPopoverOpen}
                onPopoverClose={() => setIsFilterPopoverOpen(false)}
              />
            )}
          </>
        ) : (
          <Tooltip content="Expandir" side="bottom">
            <button
              onClick={onToggle}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: 'var(--theme-sidebar-text)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              data-testid="chat-sidebar-expand"
            >
              <Icon name="chevron-right" className="h-[18px] w-[18px]" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Divider após tabs */}
      {!expanded && (
        <div
          className="border-b"
          style={{ borderColor: 'var(--theme-sidebar-border)' }}
        />
      )}

      {/* Conversations/Contacts List */}
      <div
        className={`flex-1 overflow-y-auto ${!expanded ? 'sidebar-scrollbar-hidden' : ''}`}
      >
        {/* ✅ Ocultar scrollbar quando sidebar recolhida */}
        {!expanded && (
          <style>{`
            .sidebar-scrollbar-hidden {
              scrollbar-width: none; /* Firefox */
              -ms-overflow-style: none; /* IE/Edge */
            }
            .sidebar-scrollbar-hidden::-webkit-scrollbar {
              display: none; /* Chrome/Safari */
            }
          `}</style>
        )}

        {/* ✅ View de Contatos */}
        {currentView === 'contacts' && (
          isLoadingContacts ? (
            <div
              className="p-4 text-center text-sm"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Carregando contatos...
            </div>
          ) : contactsData?.items && contactsData.items.length > 0 ? (
            contactsData.items.map(renderContactItem)
          ) : (
            <div className="p-4 text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ backgroundColor: 'var(--theme-button-secondary)' }}
              >
                <Icon name="user" className="w-8 h-8"
                  style={{ color: 'var(--theme-muted-text)' }} />
              </div>
              {expanded && (
                <>
                  <h4
                    className="font-semibold mb-1"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    {filters.search ? 'Nenhum contato encontrado' : 'Nenhum contato'}
                  </h4>
                  <p
                    className="text-sm"
                    style={{ color: 'var(--theme-muted-text)' }}
                  >
                    {filters.search ? 'Tente buscar por outro nome ou número' : 'Adicione contatos para iniciar conversas'}
                  </p>
                </>
              )}
            </div>
          )
        )}

        {/* ✅ View de IA - Infinite Scroll */}
        {currentView === 'ai' && (
          <ConversationList
            conversationType="ai"
            selectedConversationId={selectedConversationId ?? undefined}
            onSelectConversation={onSelectConversation}
            expanded={expanded}
            filters={effectiveFilters}
            searchQuery={filters.search || ''}
          />
        )}

        {/* ✅ View de Chats Individuais - Infinite Scroll */}
        {currentView === 'chats' && (
          <ConversationList
            conversationType="individual"
            selectedConversationId={selectedConversationId ?? undefined}
            onSelectConversation={onSelectConversation}
            expanded={expanded}
            filters={effectiveFilters}
            searchQuery={filters.search || ''}
          />
        )}

        {/* ✅ View de Grupos - Infinite Scroll */}
        {currentView === 'groups' && (
          <ConversationList
            conversationType="group"
            selectedConversationId={selectedConversationId ?? undefined}
            onSelectConversation={onSelectConversation}
            expanded={expanded}
            filters={effectiveFilters}
            searchQuery={filters.search || ''}
          />
        )}

        {/* ✅ View de Histórico - Agrupado por (contato, canal), server-side + infinite scroll */}
        {currentView === 'history' && (
          <HistoryList
            onSelectGroup={(contactId, channelId) => navigate(`/history/${contactId}/${channelId}`)}
            expanded={expanded}
            searchQuery={filters.search || ''}
          />
        )}
      </div>

      {/* Contact Dialogs */}
      <AddContactDialog
        isOpen={isAddContactDialogOpen}
        onClose={() => setIsAddContactDialogOpen(false)}
        onSave={handleSaveContact}
        isLoading={isCreatingContact}
      />

      <EditContactDialog
        isOpen={isEditContactDialogOpen}
        onClose={() => {
          setIsEditContactDialogOpen(false);
          setSelectedContact(null);
        }}
        onSave={handleUpdateContact}
        contact={selectedContact}
        isLoading={isUpdatingContact}
      />

      <DeleteContactDialog
        isOpen={isDeleteContactDialogOpen}
        onClose={() => {
          setIsDeleteContactDialogOpen(false);
          setSelectedContact(null);
        }}
        onConfirm={handleConfirmDelete}
        contact={selectedContact}
        isLoading={isDeletingContact}
      />

      <MergeContactsDialog
        isOpen={isMergeContactsDialogOpen}
        onClose={() => setIsMergeContactsDialogOpen(false)}
        onMerge={handleMergeContacts}
        contacts={contactsData?.items || []}
        isLoading={isMergingContacts}
      />

      {/* Iniciar Atendimento — montado só com contato presente (a modal acessa contact.identifiers no corpo) */}
      {contactForConversation && (
        <StartConversationDialog
          open={isStartConversationOpen}
          onOpenChange={(open) => {
            setIsStartConversationOpen(open);
            if (!open) setContactForConversation(null);
          }}
          contact={{
            id: contactForConversation.id,
            name: contactForConversation.name,
            identifiers: contactForConversation.identifiers,
          }}
        />
      )}

    </div>
  );
};

export default ChatSidebarLeft;
